var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc.js";
import duration from "dayjs/plugin/duration.js";
import weekOfYear from "dayjs/plugin/weekOfYear.js";
import updateLocale from "dayjs/plugin/updateLocale.js";
const DEFAULT_DATE_FORMAT = "YYYY-MM-DD HH:mm:ss";
dayjs.extend(utc);
dayjs.extend(weekOfYear);
dayjs.extend(duration);
dayjs.extend(updateLocale);
dayjs.updateLocale("en", { weekStart: 1 });
dayjs.updateLocale("ko", { weekStart: 1 });
function extractSuffix(timeframe) {
  return timeframe.slice(-1);
}
__name(extractSuffix, "extractSuffix");
Date.prototype.diff = function(dt, unit) {
  return dayjs(this).diff(dt, unit);
};
const dateFormat = /* @__PURE__ */ __name((value, fmt = DEFAULT_DATE_FORMAT) => {
  const val = typeof value === "object" ? value.valueOf() : value;
  return dayjs.utc(val).local().format(fmt);
}, "dateFormat");
Date.prototype.format = function(fmt = DEFAULT_DATE_FORMAT) {
  return dateFormat(this, fmt);
};
function getTimeframeIntervalUnit(timeframe) {
  const unit = extractSuffix(timeframe);
  const interval = Number(timeframe.replace(unit, ""));
  return { interval, unit };
}
__name(getTimeframeIntervalUnit, "getTimeframeIntervalUnit");
function getStartOfTimeframe(timestamp, timeframe) {
  let dj = dayjs.utc(timestamp);
  const { interval, unit } = getTimeframeIntervalUnit(timeframe);
  if (unit === "m") {
    const val = Math.floor(dj.minute() / interval) * interval;
    dj = dj.minute(val).startOf(unit);
  } else if (unit === "h") {
    const val = Math.floor(dj.hour() / interval) * interval;
    dj = dj.hour(val).startOf(unit);
  } else if (unit === "w") {
    const val = Math.floor(dj.week() / interval) * interval;
    dj = dj.week(val).startOf(unit);
  } else if (unit === "M") {
    const val = Math.floor(dj.month() / interval) * interval;
    dj = dj.month(val).startOf(unit);
  } else if (unit === "d") {
    const du = dayjs.duration(dj.diff(dayjs.utc(0)));
    const days = Math.floor(du.asDays()) / interval * interval;
    dj = dayjs.utc(0).add(dayjs.duration({ days }));
  }
  return dj.valueOf();
}
__name(getStartOfTimeframe, "getStartOfTimeframe");
function isTimeframeTime(dt, timeframe) {
  const dj = dayjs.utc(dt);
  const day = dj.day();
  const hour = dj.hour();
  const minute = dj.minute();
  const week = dj.week();
  const month = dj.month();
  const suffix = extractSuffix(timeframe);
  if (suffix === "m") {
    if (minute % Number(timeframe.replace("m", "")) === 0) {
      return true;
    }
  } else if (suffix === "h") {
    if (hour % Number(timeframe.replace("h", "")) === 0 && minute === 0) {
      return true;
    }
  } else if (suffix === "d") {
    if (day % Number(timeframe.replace("d", "")) === 0 && hour === 0 && minute === 0) {
      return true;
    }
  } else if (suffix === "w") {
    if (week % Number(timeframe.replace("w", "")) === 0 && dt.getDay() === 0 && hour === 0 && minute === 0) {
      return true;
    }
  } else if (suffix === "M") {
    if (month % Number(timeframe.replace("M", "")) === 0 && day === 1 && hour === 0 && minute === 0) {
      return true;
    }
  }
  return false;
}
__name(isTimeframeTime, "isTimeframeTime");
function getTimeframeDiffCount(timeframe, fromTime, toTime) {
  const toDt = toTime ? dayjs.utc(toTime) : dayjs.utc();
  const fromDt = dayjs.utc(fromTime);
  const suffix = extractSuffix(timeframe);
  const interval = Number(timeframe.replace(suffix, ""));
  const count = toDt.diff(fromDt, suffix);
  return Math.floor(count / interval);
}
__name(getTimeframeDiffCount, "getTimeframeDiffCount");
const getTimeframeMilliseconds = /* @__PURE__ */ __name((timeframe) => {
  const suffix = extractSuffix(timeframe);
  const count = Number(timeframe.replace(suffix, ""));
  return dayjs.duration(count, suffix).asMilliseconds();
}, "getTimeframeMilliseconds");
function getDuration(elapsedTime, maxUnit = "year") {
  const dur = dayjs.duration(elapsedTime);
  const years = dur.years();
  const months = dur.months();
  const days = dur.days();
  const hours = dur.hours();
  const minutes = dur.minutes();
  const seconds = dur.seconds();
  const milliseconds = dur.milliseconds();
  if (maxUnit === "year") {
    return { years, months, days, hours, minutes, seconds, milliseconds };
  }
  if (maxUnit === "month") {
    return { years: 0, months: Math.floor(dur.asMonths()), days, hours, minutes, seconds, milliseconds };
  }
  if (maxUnit === "day") {
    return { years: 0, months: 0, days: Math.floor(dur.asDays()), hours, minutes, seconds, milliseconds };
  }
  if (maxUnit === "hour") {
    return { years: 0, months: 0, days: 0, hours: Math.floor(dur.asHours()), minutes, seconds, milliseconds };
  }
  if (maxUnit === "minute") {
    return { years: 0, months: 0, days: 0, hours: 0, minutes: Math.floor(dur.asMinutes()), seconds, milliseconds };
  }
  if (maxUnit === "second") {
    return { years: 0, months: 0, days: 0, hours: 0, minutes: 0, seconds: Math.floor(dur.asSeconds()), milliseconds };
  }
  return {
    years: 0,
    months: 0,
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: Math.floor(dur.asMilliseconds())
  };
}
__name(getDuration, "getDuration");
const toElapsedMinutes = /* @__PURE__ */ __name((elapsedTime) => {
  return Math.floor(elapsedTime / 6e4);
}, "toElapsedMinutes");
const toElapsedSeconds = /* @__PURE__ */ __name((elapsedTime) => {
  return Math.floor(elapsedTime / 1e3);
}, "toElapsedSeconds");
const getElapsedTime = /* @__PURE__ */ __name((dtStart, dtEnd) => {
  const endTime = dtEnd ? typeof dtEnd === "number" ? dtEnd : dtEnd.getTime() : Date.now();
  const elapsedTime = endTime - (typeof dtStart === "number" ? dtStart : dtStart.getTime());
  return elapsedTime;
}, "getElapsedTime");
const getElapsedMinutes = /* @__PURE__ */ __name((dt) => {
  return toElapsedMinutes(getElapsedTime(dt));
}, "getElapsedMinutes");
const getElapsedSeconds = /* @__PURE__ */ __name((dt) => {
  return toElapsedSeconds(getElapsedTime(dt));
}, "getElapsedSeconds");
const formatDuration = /* @__PURE__ */ __name((elapsedTime, formatString = "HH:mm:ss") => {
  return dayjs.duration(elapsedTime).format(formatString);
}, "formatDuration");
export {
  DEFAULT_DATE_FORMAT,
  dateFormat,
  formatDuration,
  getDuration,
  getElapsedMinutes,
  getElapsedSeconds,
  getElapsedTime,
  getStartOfTimeframe,
  getTimeframeDiffCount,
  getTimeframeIntervalUnit,
  getTimeframeMilliseconds,
  isTimeframeTime,
  toElapsedMinutes,
  toElapsedSeconds
};
//# sourceMappingURL=date.js.map
